import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Container,
  Dialog,
  DialogContent,
  Fab,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RainbowFunEditor from '../webadmin/WebEditor';
import axiosPublic from '../../api/axiosPublic';
import { getDefaultWebsitePages } from '../webadmin/DataInitializer';
import { getRootSubdomain } from '../utils/subdomain';
import { isUserAuthenticated } from '../../utils/authSession';
import { ModernPageLoader, MotionHoverCard, MotionSection, iconBubbleSx } from './ModernUiKit';
import { PUBLIC_BRAND_THEME } from './publicBrandTheme';

const parseContent = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return JSON.parse(trimmed);
    } catch (_error) {
      return null;
    }
  }

  if (typeof value === 'object') {
    return value;
  }

  return null;
};

const getFallbackSidebarData = () => {
  const schoolName = localStorage.getItem('schoolName') || 'Your School';
  const page = getDefaultWebsitePages(schoolName).find((item) => item.pageSlug === 'sidebar');
  return page?.content || {};
};

const normalizeSidebarContent = (rawSidebar, fallbackSidebar = {}) => {
  const source = rawSidebar?.sidebar && typeof rawSidebar.sidebar === 'object' ? rawSidebar.sidebar : rawSidebar;

  return {
    school: source?.school && typeof source.school === 'object' ? source.school : fallbackSidebar.school || {},
    announcements: Array.isArray(source?.announcements) ? source.announcements : fallbackSidebar.announcements || [],
    news: Array.isArray(source?.news) ? source.news : fallbackSidebar.news || [],
    holidays: Array.isArray(source?.holidays) ? source.holidays : fallbackSidebar.holidays || [],
    gallery: Array.isArray(source?.gallery) ? source.gallery : fallbackSidebar.gallery || [],
  };
};

const sectionMeta = {
  announcements: {
    title: 'Announcements',
    subtitle: 'Latest official notices and school-wide updates.',
    icon: <CampaignRoundedIcon sx={{ fontSize: 18 }} />,
    path: '/announcements',
    hero:
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1800&q=80',
    fallbacks: [
      'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=1200&q=80',
    ],
  },
  news: {
    title: 'News',
    subtitle: 'Recent stories, achievements, and campus highlights.',
    icon: <ArticleRoundedIcon sx={{ fontSize: 18 }} />,
    path: '/news',
    hero:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1800&q=80',
    fallbacks: [
      'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?auto=format&fit=crop&w=1200&q=80',
    ],
  },
  holidays: {
    title: 'Holidays',
    subtitle: 'Important dates and school holiday calendar.',
    icon: <EventAvailableRoundedIcon sx={{ fontSize: 18 }} />,
    path: '/holidays',
    hero:
      'https://images.unsplash.com/photo-1464349153735-7db50ed83c84?auto=format&fit=crop&w=1800&q=80',
    fallbacks: [
      'https://images.unsplash.com/photo-1506784365847-bbad939e9335?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?auto=format&fit=crop&w=1200&q=80',
    ],
  },
  gallery: {
    title: 'Gallery',
    subtitle: 'Photo stories from classes, events, and student life.',
    icon: <PhotoLibraryRoundedIcon sx={{ fontSize: 18 }} />,
    path: '/gallery',
    hero:
      'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=1800&q=80',
    fallbacks: [
      'https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&w=1200&q=80',
    ],
  },
};

const menuItems = [
  sectionMeta.announcements,
  sectionMeta.news,
  sectionMeta.holidays,
  sectionMeta.gallery,
];
const BRAND = PUBLIC_BRAND_THEME;

const formatDate = (value) => {
  if (!value) {
    return 'Date not set';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
};

const WebUpdatePage = ({ sectionKey }) => {
  const tenantKey = getRootSubdomain();
  const [isAdmin, setIsAdmin] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [sidebarData, setSidebarData] = useState(
    normalizeSidebarContent(getFallbackSidebarData(), getFallbackSidebarData()),
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const activeMeta = sectionMeta[sectionKey] || sectionMeta.announcements;

  useEffect(() => {
    setIsAdmin(isUserAuthenticated());
    if (!tenantKey) {
      window.location.href = '/hello';
    }
  }, [tenantKey]);

  useEffect(() => {
    if (!tenantKey) {
      return;
    }

    const fallbackSidebar = getFallbackSidebarData();

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosPublic.get(`/api/${tenantKey}/pages/sidebar`);
        const parsed = parseContent(response.data?.content ?? response.data);
        setSidebarData(normalizeSidebarContent(parsed, fallbackSidebar));
        setPageData(response.data);
      } catch (fetchError) {
        console.error('Error loading sidebar page:', fetchError);
        setSidebarData(normalizeSidebarContent(fallbackSidebar, fallbackSidebar));
        setError('Showing default content because update data could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantKey]);

  const items = useMemo(() => {
    const value = sidebarData?.[sectionKey];
    return Array.isArray(value) ? value : [];
  }, [sectionKey, sidebarData]);

  const galleryItems = Array.isArray(sidebarData?.gallery) ? sidebarData.gallery : [];

  const getItemImage = (item, index) =>
    item?.fileUrl ||
    item?.image ||
    galleryItems[index % (galleryItems.length || 1)]?.fileUrl ||
    activeMeta.fallbacks[index % activeMeta.fallbacks.length];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pb: 3.2,
        bgcolor: '#F8FAFC',
        backgroundImage:
          'radial-gradient(circle at 8% -10%, rgba(30,47,99,0.12), transparent 38%), radial-gradient(circle at 96% -8%, rgba(244,124,60,0.12), transparent 36%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 60%, #FFFFFF 100%)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 210, md: 270 },
          overflow: 'hidden',
          borderBottom: `1px solid ${alpha(BRAND.primary, 0.18)}`,
          mb: 1.8,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(120deg, rgba(30,47,99,0.88) 0%, rgba(30,47,99,0.72) 56%, rgba(244,124,60,0.45) 120%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 'auto 0 0 0',
            height: '4px',
            background: `linear-gradient(90deg, ${BRAND.accent} 0%, ${BRAND.accentDeep} 100%)`,
          },
        }}
      >
        <Box
          component="img"
          src={activeMeta.hero}
          alt={activeMeta.title}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(50%)',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', py: { xs: 2.4, md: 3 } }}>
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.8 }}>
            <Chip
              icon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
              size="small"
              label="School Updates"
              sx={{
                bgcolor: alpha('#FFFFFF', 0.88),
                color: BRAND.accent,
                border: `1px solid ${alpha('#FFFFFF', 0.6)}`,
                fontWeight: 760,
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.55 }}>
            <Box sx={{ ...iconBubbleSx, bgcolor: alpha('#FFFFFF', 0.85) }}>{activeMeta.icon}</Box>
            <Typography variant="h3" sx={{ color: '#F8FAFF' }}>
              {activeMeta.title}
            </Typography>
          </Stack>
          <Typography variant="body1" sx={{ color: alpha('#F8FAFF', 0.93), maxWidth: 760 }}>
            {activeMeta.subtitle}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl">
        <MotionSection>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 0,
              border: 'none',
              boxShadow: '0 14px 34px rgba(15, 23, 42, 0.08)',
              mb: 2.4,
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(243,249,255,0.95) 100%)',
            }}
          >
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              {menuItems.map((menu) => (
                <Chip
                  key={`updates-nav-${menu.path}`}
                  label={menu.title}
                  component={RouterLink}
                  to={menu.path}
                  clickable
                  variant={menu.path === activeMeta.path ? 'filled' : 'outlined'}
                  sx={{
                    color: menu.path === activeMeta.path ? '#FFFFFF' : BRAND.primary,
                    bgcolor: menu.path === activeMeta.path ? BRAND.primary : 'transparent',
                    borderColor: alpha(BRAND.primary, 0.22),
                    '&:hover': {
                      bgcolor: menu.path === activeMeta.path ? BRAND.primaryDark : alpha(BRAND.primary, 0.08),
                    },
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </MotionSection>

        {loading && <ModernPageLoader label={`Loading ${activeMeta.title.toLowerCase()}...`} />}

        {error && !loading && (
          <Alert severity="warning" sx={{ mb: 1.4 }}>
            {error}
          </Alert>
        )}

        {!loading && sectionKey !== 'gallery' && (
          <Grid container spacing={2.6}>
            {items.map((item, index) => (
              <Grid item xs={12} md={6} key={`${sectionKey}-${item?.id || index}`}>
                <MotionHoverCard>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 0,
                      border: 'none',
                      boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
                      height: '100%',
                    }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Box
                        component="img"
                        src={getItemImage(item, index)}
                        alt={item?.title || activeMeta.title}
                        sx={{
                          width: { xs: '100%', sm: 188 },
                          height: { xs: 148, sm: 132 },
                          borderRadius: 0,
                          objectFit: 'cover',
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mb: 0.5 }}>
                          <Box sx={iconBubbleSx}>{activeMeta.icon}</Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 760 }}>
                            {item?.title || activeMeta.title}
                          </Typography>
                        </Stack>

                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65, mb: 0.4 }}>
                          {item?.content || item?.description || 'No details added yet.'}
                        </Typography>

                        <Typography variant="caption" sx={{ color: '#7A2E36', fontWeight: 700 }}>
                          {sectionKey === 'holidays' ? formatDate(item?.date) : formatDate(item?.createdAt || item?.uploadedAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </MotionHoverCard>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && sectionKey === 'gallery' && (
          <Grid container spacing={2.4}>
            {items.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={`gallery-item-${item?.id || index}`}>
                <MotionHoverCard>
                  <Paper
                    variant="outlined"
                    sx={{
                      overflow: 'hidden',
                      borderRadius: 0,
                      border: 'none',
                      boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
                    }}
                  >
                    <Box
                      component="img"
                      src={getItemImage(item, index)}
                      alt={item?.title || 'Gallery image'}
                      sx={{ width: '100%', height: 238, objectFit: 'cover' }}
                    />
                    <Box sx={{ p: 1.25 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 760 }}>
                        {item?.title || 'Gallery image'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {item?.description || 'School moments and campus highlights.'}
                      </Typography>
                    </Box>
                  </Paper>
                </MotionHoverCard>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && !items.length && (
          <Paper
            variant="outlined"
            sx={{
              p: 1.8,
              borderRadius: 0,
              border: 'none',
              boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No {activeMeta.title.toLowerCase()} added yet.
            </Typography>
          </Paper>
        )}
      </Container>

      {isAdmin && tenantKey && (
        <Fab
          size="medium"
          color="primary"
          aria-label="edit updates"
          onClick={() => setEditorOpen(true)}
          sx={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1200 }}
        >
          <EditIcon fontSize="small" />
        </Fab>
      )}

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 1.2 }}>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.6 }}>
            <IconButton size="small" onClick={() => setEditorOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <RainbowFunEditor
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            initialData={pageData?.content ? parseContent(pageData.content) : getFallbackSidebarData()}
            pageSlug="sidebar"
            componentKey="sidebar"
            subdomain={tenantKey || ''}
            onSave={(updatedData) => {
              const parsed = parseContent(updatedData);
              setSidebarData(normalizeSidebarContent(parsed, getFallbackSidebarData()));
              setSnackbar({ open: true, message: `${activeMeta.title} page updated successfully.`, severity: 'success' });
            }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export const WebAnnouncementsPage = () => <WebUpdatePage sectionKey="announcements" />;
export const WebNewsPage = () => <WebUpdatePage sectionKey="news" />;
export const WebHolidaysPage = () => <WebUpdatePage sectionKey="holidays" />;
export const WebGalleryPage = () => <WebUpdatePage sectionKey="gallery" />;
